import React, { useCallback, useMemo, useState } from "react";

import { generatePracticalExamSlots } from "../../api/practicalExamSlotsService";
import {
   normalizePracticalExamSector,
   PRACTICAL_EXAM_DEFAULT_CAPACITY,
   PRACTICAL_EXAM_MAX_CAPACITY,
   PRACTICAL_EXAM_SECTOR_OPTIONS,
   PRACTICAL_EXAM_TIME_GRID_COLUMNS,
   PRACTICAL_EXAM_TIME_OPTIONS,
} from "../../constants/practicalExamCalendar";
import usePaintToggleGrid from "../../hooks/usePaintToggleGrid";
import {
   buildPracticalExamTimeOccupancy,
   readOccupiedEntriesForDate,
} from "./practicalExamSlotOccupancy";
import {
   buildSelectableInstructorOptions,
   filterInstructorOptions,
   INSTRUCTOR_SEARCH_LIMIT,
} from "./practicalExamInstructorHelpers";

const pad2 = (value) => String(value).padStart(2, "0");
const DEFAULT_TIMES = PRACTICAL_EXAM_TIME_OPTIONS.join(", ");
const WEEKDAY_OPTIONS = [
   { value: 1, label: "Lu" },
   { value: 2, label: "Ma" },
   { value: 3, label: "Mi" },
   { value: 4, label: "Jo" },
   { value: 5, label: "Vi" },
   { value: 6, label: "Sâ" },
   { value: 0, label: "Du" },
];
const QUANTITY_MODE_OPTIONS = [
   { value: "single", label: "O rezervare" },
   { value: "multiple", label: "Mai multe în zi" },
   { value: "period", label: "Perioadă" },
];

function dateKeyFromDate(date) {
   return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
      date.getDate(),
   )}`;
}

function parseDateKey(value) {
   const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
   if (!match) return null;

   const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
   return Number.isNaN(date.getTime()) ? null : date;
}

function parseMonthKey(value) {
   const match = /^(\d{4})-(\d{2})$/.exec(String(value || ""));
   if (!match) return new Date();

   const year = Number(match[1]);
   const monthIndex = Number(match[2]) - 1;
   if (!Number.isInteger(year) || monthIndex < 0 || monthIndex > 11) {
      return new Date();
   }

   return new Date(year, monthIndex, 1);
}

function normalizeTime(value) {
   const match = String(value || "").match(/(\d{1,2}):(\d{2})/);
   if (!match) return "";
   return `${pad2(Number(match[1]))}:${match[2]}`;
}

function parseTimes(value) {
   return String(value || "")
      .split(/[\s,;]+/)
      .map(normalizeTime)
      .filter(Boolean)
      .filter((time, index, list) => list.indexOf(time) === index);
}

function normalizeDaysOfWeek(value) {
   const list = Array.isArray(value) ? value : [];
   const out = [];

   list.forEach((item) => {
      const day = Number(item);
      if (!Number.isInteger(day) || day < 0 || day > 6 || out.includes(day)) {
         return;
      }
      out.push(day);
   });

   return out;
}

function compareDayOfWeek(a, b) {
   const order = [1, 2, 3, 4, 5, 6, 0];
   return order.indexOf(a) - order.indexOf(b);
}

function normalizeQuantityMode(value) {
   const mode = String(value || "").trim();
   return ["single", "multiple", "period"].includes(mode) ? mode : "single";
}

function buildInitialForm(monthKey, initialValues = {}) {
   const monthDate = parseMonthKey(monthKey);
   const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
   const lastDay = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
   const startDate = initialValues.startDate || initialValues.date || dateKeyFromDate(firstDay);
   const endDate = initialValues.endDate || initialValues.date || dateKeyFromDate(lastDay);

   return {
      daysOfWeek: initialValues.daysOfWeek || [1, 3, 5],
      times: initialValues.times != null ? initialValues.times : DEFAULT_TIMES,
      sector: normalizePracticalExamSector(initialValues.sector, "Botanica"),
      gearbox: initialValues.gearbox || "manual",
      capacity: String(initialValues.capacity || PRACTICAL_EXAM_DEFAULT_CAPACITY),
      startDate,
      endDate,
      instructorId: String(initialValues.instructorId ?? ""),
   };
}

function buildPayload(form, { quantityMode = "single", selectedTimes = [] } = {}) {
   const mode = normalizeQuantityMode(quantityMode);
   const selectedDate = parseDateKey(form.startDate);
   const endDate = parseDateKey(form.endDate);
   const selectedTimeList = Array.isArray(selectedTimes) && selectedTimes.length
      ? selectedTimes
      : parseTimes(form.times);
   const times = PRACTICAL_EXAM_TIME_OPTIONS.filter((time) =>
      selectedTimeList.includes(time),
   );
   const payloadTimes = mode === "single" ? times.slice(0, 1) : times;

   if (!selectedDate) {
      throw new Error("Alege data pentru generare.");
   }
   if (mode === "period" && !endDate) {
      throw new Error("Alege data finală pentru perioadă.");
   }
   if (mode === "period" && endDate < selectedDate) {
      throw new Error("Data finală trebuie să fie după data de început.");
   }

   const periodDays = normalizeDaysOfWeek(form.daysOfWeek).sort(compareDayOfWeek);
   if (mode === "period" && !periodDays.length) {
      throw new Error("Alege cel puțin o zi din săptămână.");
   }

   const capacity = Number(form.capacity);
   if (!Number.isFinite(capacity) || capacity < 1) {
      throw new Error("Capacitatea minimă este 1.");
   }
   if (capacity > PRACTICAL_EXAM_MAX_CAPACITY) {
      throw new Error(`Capacitatea maximă este ${PRACTICAL_EXAM_MAX_CAPACITY}.`);
   }

   return {
      daysOfWeek: mode === "period" ? periodDays : [selectedDate.getDay()],
      times: payloadTimes,
      sector: normalizePracticalExamSector(form.sector, "Botanica"),
      gearbox: form.gearbox,
      capacity,
      startDate: form.startDate,
      endDate: mode === "period" ? form.endDate : form.startDate,
      instructorId: form.instructorId ? Number(form.instructorId) : "",
   };
}

export default function PracticalExamGenerateSlotsPopup({
   monthKey,
   initialValues = {},
   instructorOptions = [],
   occupiedTimesByDate = {},
   onSaved,
   onAlert,
   onClose,
}) {
   const [form, setForm] = useState(() => buildInitialForm(monthKey, initialValues));
   const [quantityMode, setQuantityMode] = useState(() =>
      normalizeQuantityMode(initialValues.quantityMode),
   );
   const [saving, setSaving] = useState(false);
   const [error, setError] = useState("");
   const [view, setView] = useState("form");
   const [qInstructor, setQInstructor] = useState("");
   const busy = saving;

   const reportError = (message) => {
      const text = String(message || "A apărut o eroare.").trim();
      if (typeof onAlert === "function") {
         onAlert("error", text);
      } else {
         setError(text);
      }
   };

   const selectableInstructorOptions = useMemo(
      () =>
         buildSelectableInstructorOptions({
            instructorOptions,
            selectedId: form.instructorId,
            fallbackLabel: initialValues.instructorName || "Instructor",
         }),
      [form.instructorId, initialValues.instructorName, instructorOptions],
   );
   const hasInstructorOptions = selectableInstructorOptions.length > 0;
   const selectedInstructorOption = useMemo(
      () =>
         selectableInstructorOptions.find(
            (option) => String(option.value) === String(form.instructorId),
         ) || null,
      [form.instructorId, selectableInstructorOptions],
   );
   const instructorSearch = useMemo(
      () => filterInstructorOptions(selectableInstructorOptions, qInstructor),
      [qInstructor, selectableInstructorOptions],
   );
   const filteredInstructorOptions = instructorSearch.items;
   const instructorResultsLimited = instructorSearch.limited;
   const isSingleInstructorMatch = instructorSearch.isSingleMatch;

   const updateField = (field, value) => {
      setForm((prev) => ({ ...prev, [field]: value }));
   };

   const isPeriodMode = quantityMode === "period";

   const updateSlotDate = (value) => {
      const parsed = parseDateKey(value);
      setForm((prev) => {
         const previousEndDate = parseDateKey(prev.endDate);
         const keepEndDate =
            isPeriodMode && parsed && previousEndDate && previousEndDate >= parsed;

         return {
            ...prev,
            startDate: value,
            endDate: isPeriodMode ? (keepEndDate ? prev.endDate : value) : value,
            daysOfWeek:
               isPeriodMode
                  ? normalizeDaysOfWeek(prev.daysOfWeek)
                  : parsed
                    ? [parsed.getDay()]
                    : prev.daysOfWeek,
         };
      });
   };

   const updateEndDate = (value) => {
      setForm((prev) => ({
         ...prev,
         endDate: value,
      }));
   };

   const selectedDateKey = form.startDate;
   // Ora e blocată doar dacă există deja un slot identic (sector + cutie +
   // instructor) în ziua aleasă; alte sloturi la aceeași oră doar o marchează.
   const occupiedEntriesForDate = useMemo(
      () => readOccupiedEntriesForDate(occupiedTimesByDate, selectedDateKey),
      [occupiedTimesByDate, selectedDateKey],
   );
   const timeOccupancy = useMemo(
      () =>
         isPeriodMode
            ? { exact: new Set(), elsewhere: new Set() }
            : buildPracticalExamTimeOccupancy(occupiedEntriesForDate, {
                 sector: form.sector,
                 gearbox: form.gearbox,
                 instructorId: form.instructorId,
              }),
      [
         form.gearbox,
         form.instructorId,
         form.sector,
         isPeriodMode,
         occupiedEntriesForDate,
      ],
   );
   const selectableOccupiedTimes = timeOccupancy.exact;
   const takenElsewhereTimes = timeOccupancy.elsewhere;
   const freeTimes = useMemo(
      () =>
         PRACTICAL_EXAM_TIME_OPTIONS.filter(
            (time) => !selectableOccupiedTimes.has(time),
         ),
      [selectableOccupiedTimes],
   );
   const rawSelectedTimes = parseTimes(form.times);
   const selectedTimes = PRACTICAL_EXAM_TIME_OPTIONS.filter(
      (time) =>
         rawSelectedTimes.includes(time) && !selectableOccupiedTimes.has(time),
   );
   const activeSelectedTimes =
      quantityMode === "single" ? selectedTimes.slice(0, 1) : selectedTimes;
   const selectedDaysOfWeek = normalizeDaysOfWeek(form.daysOfWeek);
   const startDateValue = parseDateKey(form.startDate);
   const endDateValue = parseDateKey(form.endDate);
   const hasValidDateRange =
      !isPeriodMode ||
      (startDateValue && endDateValue && endDateValue >= startDateValue);

   const selectQuantityMode = (mode) => {
      if (busy) return;
      const nextMode = normalizeQuantityMode(mode);
      const fallbackTime = activeSelectedTimes[0] || freeTimes[0] || "";
      setQuantityMode(nextMode);
      setForm((prev) => ({
         ...prev,
         endDate:
            nextMode === "period" ? prev.endDate || prev.startDate : prev.startDate,
         daysOfWeek:
            nextMode === "period"
               ? normalizeDaysOfWeek(prev.daysOfWeek).length
                  ? normalizeDaysOfWeek(prev.daysOfWeek)
                  : parseDateKey(prev.startDate)
                    ? [parseDateKey(prev.startDate).getDay()]
                    : [1, 3, 5]
               : parseDateKey(prev.startDate)
                 ? [parseDateKey(prev.startDate).getDay()]
                 : prev.daysOfWeek,
         times:
            nextMode === "single"
               ? fallbackTime
               : activeSelectedTimes.length
                 ? activeSelectedTimes.join(", ")
                 : fallbackTime,
      }));
   };

   const toggleWeekday = (dayValue) => {
      setForm((prev) => {
         const current = new Set(normalizeDaysOfWeek(prev.daysOfWeek));
         if (current.has(dayValue)) {
            current.delete(dayValue);
         } else {
            current.add(dayValue);
         }

         return {
            ...prev,
            daysOfWeek: Array.from(current).sort(compareDayOfWeek),
         };
      });
   };

   const applyTimePaint = useCallback(
      (time, mode) => {
         if (selectableOccupiedTimes.has(time)) return;
         setForm((prev) => {
            if (quantityMode === "single") {
               if (mode === "deselect") return prev;
               return {
                  ...prev,
                  times: time,
               };
            }

            const current = new Set(parseTimes(prev.times));
            if (mode === "select") {
               current.add(time);
            } else {
               current.delete(time);
            }

            const times = PRACTICAL_EXAM_TIME_OPTIONS.filter((option) =>
               current.has(option),
            );

            return {
               ...prev,
               times: times.join(", "),
            };
         });
      },
      [quantityMode, selectableOccupiedTimes],
   );

   const { getItemHandlers: getTimePaintHandlers } = usePaintToggleGrid({
      onApply: applyTimePaint,
      isBlocked: (time) => busy || selectableOccupiedTimes.has(time),
   });

   const openInstructorSearch = () => {
      if (busy) return;
      setQInstructor("");
      setView("instructorSearch");
   };

   const selectInstructor = (value) => {
      const option = selectableInstructorOptions.find(
         (entry) => String(entry.value) === String(value),
      );
      setForm((prev) => ({
         ...prev,
         instructorId: String(value),
         sector: normalizePracticalExamSector(option?.sector, prev.sector),
         gearbox: option?.gearbox || prev.gearbox,
      }));
      setView("form");
   };

   const handleInstructorSearchKeyDown = (event, value) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      selectInstructor(value);
   };

   const renderInstructorSearch = () => (
      <>
         <div className="createReservationPopup__search-header">
            <input
               type="text"
               className="createReservationPopup__search-input"
               placeholder="Caută instructor după nume sau telefon..."
               value={qInstructor}
               onChange={(event) => setQInstructor(event.target.value)}
               onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
               }}
               autoFocus
               disabled={busy}
            />
            <div className="createReservationPopup__search-header-actions">
               <button
                  type="button"
                  className="createReservationPopup__btn createReservationPopup__btn--normal"
                  onClick={() => setView("form")}
                  disabled={busy}
               >
                  Înapoi
               </button>
            </div>
         </div>

         {filteredInstructorOptions.length ? (
            <ul className="createReservationPopup__search-list practical-exam-interne-search-list">
               {filteredInstructorOptions.map((option) => {
                  const isActive =
                     String(option.value) === String(form.instructorId);

                  return (
                     <li
                        key={option.value}
                        className={[
                           "createReservationPopup__search-item",
                           isActive
                              ? "createReservationPopup__search-item--highlighted"
                              : "",
                           isSingleInstructorMatch
                              ? "createReservationPopup__search-item--full"
                              : "",
                        ]
                           .filter(Boolean)
                           .join(" ")}
                        role="button"
                        tabIndex={0}
                        onClick={() => selectInstructor(option.value)}
                        onKeyDown={(event) =>
                           handleInstructorSearchKeyDown(event, option.value)
                        }
                     >
                        <h3>{option.label}</h3>
                        {option.phone ? <p>{option.phone}</p> : null}
                     </li>
                  );
               })}
            </ul>
         ) : (
            <div className="createReservationPopup__search-empty">
               Nu am găsit instructori.
            </div>
         )}

         {instructorResultsLimited ? (
            <div className="practical-exam-popup__search-limit">
               Sunt afișate primele {INSTRUCTOR_SEARCH_LIMIT} rezultate. Folosește
               căutarea pentru filtrare.
            </div>
         ) : null}
      </>
   );

   const handleSubmit = async (event) => {
      event.preventDefault();
      setSaving(true);
      setError("");

      try {
         const payload = buildPayload(form, {
            quantityMode,
            selectedTimes: activeSelectedTimes,
         });
         if (!payload.times.length) {
            throw new Error("Adaugă cel puțin o oră validă.");
         }
         if (payload.instructorId === "" || payload.instructorId == null) {
            throw new Error("Alege instructorul de examen.");
         }

         const result = await generatePracticalExamSlots(payload);
         const created =
            result?.created != null ? `Sloturi create: ${result.created}` : "";
         onSaved?.(result, created || "Sloturile au fost generate.");
         onClose?.();
      } catch (err) {
         reportError(err?.message || "Nu am putut genera sloturile.");
      } finally {
         setSaving(false);
      }
   };

   const renderInputField = ({
      label,
      value,
      onChange,
      type = "text",
      inputMode,
      min,
      max,
      required = false,
   }) => (
      <label className="createReservationPopup__field createReservationPopup__field--grow-1">
         <span className="createReservationPopup__field-label">{label}</span>
         <input
            className="createReservationPopup__input"
            type={type}
            inputMode={inputMode}
            min={min}
            max={max}
            value={value}
            onChange={onChange}
            required={required}
            disabled={busy}
         />
      </label>
   );

   const renderSectorField = () => (
      <label className="createReservationPopup__field createReservationPopup__field--grow-1">
         <span className="createReservationPopup__field-label">Sector</span>
         <select
            className="createReservationPopup__input"
            value={normalizePracticalExamSector(form.sector, "Botanica")}
            onChange={(event) => updateField("sector", event.target.value)}
            required
            disabled={busy}
         >
            {PRACTICAL_EXAM_SECTOR_OPTIONS.map((sector) => (
               <option key={sector} value={sector}>
                  {sector}
               </option>
            ))}
         </select>
      </label>
   );

   const openDatePicker = (event) => {
      if (busy) return;
      const input = event.currentTarget.querySelector('input[type="date"]');
      if (!(input instanceof HTMLInputElement)) return;

      if (typeof input.showPicker === "function") {
         event.preventDefault();
         input.showPicker();
         return;
      }

      input.focus();
   };

   const renderDateField = ({ label, value, onChange, required = false }) => (
      <label
         className="createReservationPopup__field createReservationPopup__field--grow-1 createReservationPopup__field--date-picker"
         onClick={openDatePicker}
      >
         <span className="createReservationPopup__field-label">{label}</span>
         <input
            className="createReservationPopup__input"
            type="date"
            value={value}
            onChange={onChange}
            required={required}
            disabled={busy}
         />
      </label>
   );

   const renderInstructorField = () => (
      <div
         className="createReservationPopup__field createReservationPopup__field--clickable"
         role="button"
         tabIndex={busy ? -1 : 0}
         aria-disabled={busy}
         onClick={() => {
            if (!busy) openInstructorSearch();
         }}
         onKeyDown={(event) => {
            if (busy) return;
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            openInstructorSearch();
         }}
      >
         <span className="createReservationPopup__field-label">Instructor</span>
         <div className="createReservationPopup__field-line">
            <span className="createReservationPopup__field-text">
               {selectedInstructorOption
                  ? selectedInstructorOption.label
                  : "Alege instructor"}
            </span>
         </div>
         <div className="createReservationPopup__field-line">
            <span className="createReservationPopup__field-text">
               {selectedInstructorOption?.phone || "Telefon instructor"}
            </span>
         </div>
      </div>
   );

   const renderGearboxField = ({ value, onChange, name }) => (
      <div className="createReservationPopup__field createReservationPopup__field--stacked">
         <span className="createReservationPopup__field-label">Cutie</span>
         <div
            className={`practical-exam-popup__radio-wrapper practical-exam-popup__radio-wrapper--${
               value === "manual" ? "left" : "right"
            }`}
         >
            <label className="practical-exam-popup__radio-option">
               <input
                  type="radio"
                  name={name}
                  value="manual"
                  checked={value === "manual"}
                  onChange={onChange}
                  disabled={busy}
               />
               <span>Manual</span>
            </label>
            <label className="practical-exam-popup__radio-option">
               <input
                  type="radio"
                  name={name}
                  value="automatic"
                  checked={value === "automatic"}
                  onChange={onChange}
                  disabled={busy}
               />
               <span>Automat</span>
            </label>
         </div>
      </div>
   );

   const renderTimesField = () => (
      <div className="createReservationPopup__field createReservationPopup__field--stacked">
         <span className="createReservationPopup__field-label">Ore</span>
         <div
            className="practical-exam-popup__time-grid practical-exam-popup__time-grid--paint"
            style={{
               gridTemplateColumns: `repeat(${PRACTICAL_EXAM_TIME_GRID_COLUMNS}, minmax(0, 1fr))`,
            }}
         >
            {PRACTICAL_EXAM_TIME_OPTIONS.map((time) => {
               const isOccupied = selectableOccupiedTimes.has(time);
               const isTakenElsewhere =
                  !isOccupied && takenElsewhereTimes.has(time);
               const isActive = activeSelectedTimes.includes(time);
               const paintHandlers = getTimePaintHandlers(time, isActive);

               return (
                  <button
                     key={time}
                     type="button"
                     className={
                        "practical-exam-popup__time-button " +
                        (isActive ? "is-active " : "") +
                        (isOccupied ? "is-disabled " : "") +
                        (isTakenElsewhere ? "is-elsewhere" : "")
                     }
                     disabled={busy || isOccupied}
                     title={
                        isOccupied
                           ? "Există deja un slot identic (același sector, cutie și instructor) la această oră"
                           : isTakenElsewhere
                             ? "Există un slot la această oră pentru alt sector, altă cutie sau alt instructor; poți adăuga încă unul"
                             : ""
                     }
                     onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        applyTimePaint(time, isActive ? "deselect" : "select");
                     }}
                     {...paintHandlers}
                  >
                     {time}
                  </button>
               );
            })}
         </div>
      </div>
   );

   const renderQuantityTabs = () => (
      <div
         className="createReservationPopup__actions-row practical-exam-generate-popup__tabs practical-exam-generate-popup__quantity-tabs practical-exam-generate-popup__quantity-tabs--three"
         role="tablist"
         aria-label="Tip generare"
      >
         {QUANTITY_MODE_OPTIONS.map((option) => (
            <button
               key={option.value}
               type="button"
               className={[
                  "createReservationPopup__btn",
                  quantityMode === option.value
                     ? "createReservationPopup__btn--active"
                     : "createReservationPopup__btn--normal",
                  "createReservationPopup__btn--action",
               ].join(" ")}
               role="tab"
               aria-selected={quantityMode === option.value}
               onClick={() => selectQuantityMode(option.value)}
               disabled={busy}
            >
               {option.label}
            </button>
         ))}
      </div>
   );

   const renderWeekdayField = () => (
      <div className="createReservationPopup__field createReservationPopup__field--stacked practical-exam-popup__weekday-field">
         <span className="createReservationPopup__field-label">Zile</span>
         <div className="practical-exam-popup__weekdays">
            {WEEKDAY_OPTIONS.map((option) => {
               const isActive = selectedDaysOfWeek.includes(option.value);

               return (
                  <button
                     key={option.value}
                     type="button"
                     className={isActive ? "is-active" : ""}
                     onClick={() => toggleWeekday(option.value)}
                     disabled={busy}
                  >
                     {option.label}
                  </button>
               );
            })}
         </div>
      </div>
   );

   const renderSlotsForm = () => (
      <form className="practical-exam-generate-popup__form" onSubmit={handleSubmit}>
         {renderQuantityTabs()}

         <div className="createReservationPopup__group-body">
            {renderInstructorField()}

            <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
               {renderSectorField()}
               {renderInputField({
                  label: "Capacitate",
                  type: "number",
                  inputMode: "numeric",
                  min: "1",
                  max: String(PRACTICAL_EXAM_MAX_CAPACITY),
                  value: form.capacity,
                  onChange: (event) => updateField("capacity", event.target.value),
                  required: true,
               })}
            </div>

            {renderGearboxField({
               value: form.gearbox,
               name: "practical-exam-generate-gearbox",
               onChange: (event) => updateField("gearbox", event.target.value),
            })}

            {isPeriodMode ? (
               <>
                  <div className="createReservationPopup__form-row createReservationPopup__form-row--compact">
                     {renderDateField({
                        label: "De la",
                        value: form.startDate,
                        onChange: (event) => updateSlotDate(event.target.value),
                        required: true,
                     })}
                     {renderDateField({
                        label: "Până la",
                        value: form.endDate,
                        onChange: (event) => updateEndDate(event.target.value),
                        required: true,
                     })}
                  </div>

                  {renderWeekdayField()}
               </>
            ) : (
               renderDateField({
                  label: "Data",
                  value: form.startDate,
                  onChange: (event) => updateSlotDate(event.target.value),
                  required: true,
               })
            )}

            {renderTimesField()}
         </div>

         <div className="createReservationPopup__group-body">
            <div className="createReservationPopup__actions-row createReservationPopup__actions-row--two">
               <button
                  type="button"
                  className="createReservationPopup__btn createReservationPopup__btn--normal createReservationPopup__btn--action"
                  onClick={onClose}
                  disabled={busy}
               >
                  Anulează
               </button>
               <button
                  type="submit"
                  className="createReservationPopup__btn createReservationPopup__btn--save createReservationPopup__btn--action"
                  disabled={
                     busy ||
                     !hasInstructorOptions ||
                     !activeSelectedTimes.length ||
                     !freeTimes.length ||
                     !hasValidDateRange ||
                     (isPeriodMode && !selectedDaysOfWeek.length)
                  }
               >
                  {saving ? "Se generează..." : "Generează"}
               </button>
            </div>
         </div>
      </form>
   );

   return (
      <div className="createReservationPopup practical-exam-generate-popup practical-exam-generate-popup--create">
         <div className="createReservationPopup__header">
            <h3 className="createReservationPopup__title">Generează sloturi</h3>
         </div>

         <div className="createReservationPopup__content practical-exam-generate-popup__content">
            {error ? (
               <p className="instructorProfileUI__error practical-exam-popup__error">
                  {error}
               </p>
            ) : null}

            {view === "form" && !hasInstructorOptions ? (
               <p className="instructorProfileUI__error practical-exam-popup__error">
                  Nu există instructori marcați ca examen.
               </p>
            ) : null}

            {view === "instructorSearch"
               ? renderInstructorSearch()
               : renderSlotsForm()}
         </div>
      </div>
   );
}
